import React, { useState, useEffect } from "react";
import userService from "../../services/userService";
import docService from "../../services/docService";
import { Form, Button, Alert, Row, Col } from "react-bootstrap";
import imageCompression from "browser-image-compression";
import { useTranslation, Trans } from "react-i18next";

export default function UploadDocuments({ shake, userData, updatePage, user }) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({ covidVaccinated: null, tbTested: null });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    if (!userData.documents) {
      userData.documents = {
        documents: {}
      };
    }

    if (!userData.documents.documents) {
      userData.documents = {
        ...userData.documents,
        documents: {}
      };
    }
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (name != "covidVaccinated" && name != "tbTested") {
      const inputEl = e.target;

      if (!inputEl.files || inputEl.files.length === 0)
        return;

      setUploadError(null);

      const uploadName = inputEl.files[0].name;
      let uploadFile;

      const isPdf = uploadName.toLowerCase().endsWith(".pdf")
        || inputEl.files[0].type === "application/pdf";

      if (isPdf)
        uploadFile = inputEl.files[0];
      else {
        const imageFile = inputEl.files[0];

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920
        }
        try {
          uploadFile = await imageCompression(imageFile, options);
        } catch (error) {
          console.log(error);
          //compression fails on formats the browser can't decode (e.g. HEIC on
          //Android/Chrome) - upload the original file raw rather than blocking,
          //as long as it isn't unreasonably large
          if (imageFile.size <= 10 * 1024 * 1024)
            uploadFile = imageFile;
        }
      }

      if (!uploadFile) {
        setUploadError("We couldn't read that file. Please try a smaller file or take a new photo.");
        inputEl.value = "";
        return;
      }

      if (userData.applicantStatus === 'synced')
        userData.applicantStatus = "newDoc";

      const formData = new FormData();
      //third argument keeps the original filename (compression returns a
      //nameless Blob that would otherwise upload as "blob")
      formData.append('files', uploadFile, uploadName);
      formData.append('documentRef', name);
      formData.append('userData', JSON.stringify(userData));

      setUploading(true);
      try {
        const { data: docKey } = await docService.saveDocument(formData);
        setFormState({ ...formState, [name]: docKey });
        if (!userData.documents)
          userData.documents = { documents: {} };
        if (!userData.documents.documents)
          userData.documents.documents = {};
        userData.documents.documents[name] = docKey;
      } catch (error) {
        console.log(error);
        setUploadError("Your document failed to upload. Please try again. If the problem continues, contact your agency.");
        inputEl.value = "";
      } finally {
        setUploading(false);
      }
    } else {
      if (name === "covidVaccinated") {
        await userService.saveUserData(user, "documents.covidVaccinated", e.target.value);
        userData.documents.covidVaccinated = e.target.value;
      }

      if (name === "tbTested") {
        await userService.saveUserData(user, "documents.tbTested", e.target.value);
        userData.documents.tbTested = e.target.value;
      }

      setFormState({ ...formState, [e.target.name]: e.target.value });
    }
  };

  return (
    <>
      {/* Instructions  */}
      <section className="main-section">
        <h2>{t("uploadDocs.title")}</h2>
        <div className="main-section-content text-start">
          <p>{t("uploadDocs.intro")}</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="main-section">
        <h2>{t("uploadDocs.formTitle")}</h2>
        <section className="main-section-content">
          {/* Form Section - Instructions  */}
          <section className="text-start mb-5">
            <p>{t("uploadDocs.formInstructions")}</p>
            <p>{t("uploadDocs.tbInstructions")}</p>
            <hr className="my-5" />
          </section>
          {/* Form Section - Form  */}
          <Alert
            show={uploadError ? true : false}
            variant="danger"
            className="m-1">
            {uploadError}
          </Alert>
          <Alert
            show={uploading}
            variant="info"
            className="m-1">
            Uploading your document, please wait...
          </Alert>
          <Form noValidate>
            {/* ID*/}
            <section className="text-start">
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("uploadDocs.identification")}</h3>
                <em>{t("uploadDocs.required")}</em>
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.driverLicense
                    || userData.documents.documents.stateId ? true : false : false : false}
                  variant="success"
                  className="m-1">
                  {t("uploadDocs.identificationUploaded")}
                </Alert>
                <Alert
                  show={userData.documents ? userData.documents.documents ? !userData.documents.documents.driverLicense
                    && !userData.documents.documents.stateId ? true : false : false : false}
                  variant="danger"
                  className="m-1">
                  {t("uploadDocs.identificationRequired")}
                </Alert>
              </div>
              <p>{t("uploadDocs.uploadOne")}</p>
              <Form.Group
                controlId="fileDriverLic"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.driverLicense")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="driverLicense"
                  disabled={uploading}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.driverLicense ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.driverLicenseUploaded")}
                </Alert>
              </Form.Group>

              <Form.Group
                controlId="fileIdCard"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.stateId")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="stateId"
                  disabled={uploading}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.stateId ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.stateIdUploaded")}
                </Alert>
              </Form.Group>
              <hr className="my-5" />
            </section>

            {/* Birth Certificate, SSC or passport upload*/}
            <section className="text-start">
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("uploadDocs.citizenship")}</h3>
                <em>{t("uploadDocs.required")}</em>
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.birthCertificate
                    || userData.documents.documents.socialSecurityCard || userData.documents.documents.passport
                    ? true : false : false : false}
                  variant="success"
                  className="m-1">
                  {t("uploadDocs.citizenshipUploaded")}
                </Alert>
                <Alert
                  show={userData.documents ? userData.documents.documents ? !userData.documents.documents.birthCertificate
                    && !userData.documents.documents.socialSecurityCard && !userData.documents.documents.passport
                    ? true : false : false : false}
                  variant="danger"
                  className="m-1">
                  {t("uploadDocs.citizenshipRequired")}
                </Alert>
              </div>
              <p>{t("uploadDocs.uploadOne")}</p>
              <Form.Group
                controlId="fileBirthCert"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.birthCertificate")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="birthCertificate"
                  disabled={uploading}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.birthCertificate ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.birthCertificateUploaded")}
                </Alert>
              </Form.Group>

              <Form.Group controlId="fileSsc" className="mb-3 text-start">
                <Form.Label>{t("uploadDocs.socialSecurityCard")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="socialSecurityCard"
                  disabled={uploading}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.socialSecurityCard ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.socialSecurityCardUploaded")}
                </Alert>
              </Form.Group>
              <Form.Group
                controlId="filePassport"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.passport")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="passport"
                  disabled={uploading}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.passport ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.passportUploaded")}
                </Alert>
              </Form.Group>
              <hr className="my-5" />
            </section>

            {/* Covid Vaccination  */}
            <section className="text-start">
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("uploadDocs.covidVaccination")}</h3>
                <em>{t("uploadDocs.required")}</em>
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.covidVaccine
                    ? true : false : false : false}
                  variant="success"
                  className="m-1">
                  {t("uploadDocs.covidUploaded")}
                </Alert>
                <Alert
                  show={userData.documents ? userData.documents.documents ? !userData.documents.documents.covidVaccine
                    ? true : false : false : false}
                  variant="danger"
                  className="m-1">
                  {t("uploadDocs.covidRequired")}
                </Alert>
              </div>
              {/* <strong> */}
              <p>{t("uploadDocs.covidQuestion")}</p>
              {/* </strong> */}

              <Form.Group className="mb-3">
                <Form.Check
                  inline
                  type="radio"
                  label={t("common.yes")}
                  id="inline-radio-1"
                  name="covidVaccinated"
                  value="yes"
                  onChange={handleInputChange}
                  required
                  checked={formState.covidVaccinated === "yes" ? formState.covidVaccinated === "yes" : userData.documents ? userData.documents.covidVaccinated === "yes" : null}
                />
                <Form.Check
                  inline
                  type="radio"
                  label={t("common.no")}
                  id="inline-radio-2"
                  name="covidVaccinated"
                  value="no"
                  onChange={handleInputChange}
                  required
                  checked={formState.covidVaccinated === "no" ? formState.covidVaccinated === "no" : userData.documents ? userData.documents.covidVaccinated === "no" : null}
                />
              </Form.Group>

              <Form.Group
                controlId="fileCovidVax"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.covidUploadLabel")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="covidVaccine"
                  disabled={uploading}
                  // required={
                  //   formState.covidVaccinated === "no" ? false : true
                  // }
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.covidVaccine ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.covidCardUploaded")}
                </Alert>
              </Form.Group>
              <p>
                <Trans
                  i18nKey="uploadDocs.covidDisclaimer"
                  components={{ strong: <strong /> }}
                />
              </p>
              <hr className="my-5" />
            </section>

            {/* TB Skin test  */}
            <section className="text-start">
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("uploadDocs.tbSkinTest")}</h3>
                <em>{t("uploadDocs.required")}</em>
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.tbTest
                    ? true : false : false : false}
                  variant="success"
                  className="m-1">
                  {t("uploadDocs.tbUploaded")}
                </Alert>
                <Alert
                  show={userData.documents ? userData.documents.documents ? !userData.documents.documents.tbTest
                    ? true : false : false : false}
                  variant="danger"
                  className="m-1">
                  {t("uploadDocs.tbRequired")}
                </Alert>
              </div>
              {/* <strong> */}
              <p>{t("uploadDocs.tbQuestion")}</p>
              {/* </strong> */}
              <Form.Group className="mb-3">
                <Form.Check
                  inline
                  type="radio"
                  label={t("common.yes")}
                  id="tb-test-1"
                  name="tbTested"
                  value="yes"
                  onChange={handleInputChange}
                  required
                  checked={formState.tbTested === "yes" ? formState.tbTested === "yes" : userData.documents ? userData.documents.tbTested === "yes" : null}
                />
                <Form.Check
                  inline
                  type="radio"
                  label={t("common.no")}
                  id="tb-test-2"
                  name="tbTested"
                  value="no"
                  onChange={handleInputChange}
                  required
                  checked={formState.tbTested === "no" ? formState.tbTested === "no" : userData.documents ? userData.documents.tbTested === "no" : null}
                />
              </Form.Group>
              <Form.Group
                controlId="fileTbTest"
                className="mb-3 text-start"
              >
                <Form.Label>{t("uploadDocs.tbUploadLabel")}</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  name="tbTest"
                  disabled={uploading}
                  // required={formState.tbTested === "yes" ? true : false}
                  onChange={handleInputChange}
                />
                <Alert
                  show={userData.documents ? userData.documents.documents ? userData.documents.documents.tbTest ? true : false : false : false}
                  variant="success"
                  className="mt-2">
                  {t("uploadDocs.tbUploaded")}
                </Alert>
              </Form.Group>
              <p>
                <Trans
                  i18nKey="uploadDocs.tbDisclaimer"
                  components={{ strong: <strong /> }}
                />
              </p>
            </section>
            {/* <Button type="submit">Submit</Button> */}
          </Form>
        </section>
      </section>
    </>
  );

  // const handleSubmit = (e) => {
  //   e.preventDefault();

  //   let originalDocumentObject = userData.documents;

  //   if (!originalDocumentObject.documents)
  //     originalDocumentObject.documents = {};

  //   if (formState.driverLicense)
  //     originalDocumentObject.documents.driverLicense = formState.driverLicense;
  //   if (formState.stateId)
  //     originalDocumentObject.documents.stateId = formState.stateId;
  //   if (formState.birthCertificate)
  //     originalDocumentObject.documents.birthCertificate = formState.birthCertificate;
  //   if (formState.socialSecurityCard)
  //     originalDocumentObject.documents.socialSecurityCard = formState.socialSecurityCard;
  //   if (formState.passport)
  //     originalDocumentObject.documents.passport = formState.passport;
  //   if (formState.covidVaccination)
  //     originalDocumentObject.documents.covidVaccination = formState.covidVaccination;
  //   if (formState.tbTest)
  //     originalDocumentObject.documents.tbTest = formState.tbTest;

  //   if (formState.tbTested)
  //     originalDocumentObject.tbTested = formState.tbTested;

  //   if (formState.covidVaccinated)
  //     originalDocumentObject.covidVaccinated = formState.covidVaccinated;

  //   var formValid = formState.tbTest || formState.covidVaccinated || formState.covidVaccination || formState.tbTested || formState.passport
  //     || formState.socialSecurityCard || formState.birthCertificate || formState.stateId || formState.driverLicense;

  //   if (formValid === undefined) {
  //     e.preventDefault();
  //     e.stopPropagation();
  //     shake();
  //   } else {
  //     updatePage();
  //     userService.saveUserData(user, "documents", originalDocumentObject);
  //   }
  // };
}
